import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { BRANDS, branchesFor, brandName, money } from "@/lib/brands";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, Users, PawPrint } from "lucide-react";
import { toast } from "sonner";

export function NewClientDialog({ trigger, onCreated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ owner_name: "", phone: "", email: "", address: "", notes: "", brand: "", owning_branch: "" });
  const [pets, setPets] = useState([]);
  const [busy, setBusy] = useState(false);

  const addPetRow = () => setPets((p) => [...p, { _k: crypto.randomUUID(), pet_name: "", species: "Dog", breed: "", age: "" }]);
  const setPet = (i, patch) => setPets((p) => p.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const removePet = (i) => setPets((p) => p.filter((_, j) => j !== i));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f };
      if (!isAdmin) { delete payload.brand; delete payload.owning_branch; }
      const { data } = await api.post("/clients", payload);
      let created = 0;
      for (const pet of pets) {
        if (!pet.pet_name.trim()) continue;
        const { _k, ...petBody } = pet;
        await api.post(`/clients/${data.id}/pets`, { ...petBody, species: petBody.species || "Dog" });
        created++;
      }
      toast.success(`Client ${data.owner_name} created${created ? ` with ${created} pet${created > 1 ? "s" : ""}` : ""}`);
      setOpen(false);
      setF({ owner_name: "", phone: "", email: "", address: "", notes: "", brand: "", owning_branch: "" });
      setPets([]);
      onCreated?.(data);
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPets([]); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5"><Label>Owner Name *</Label>
            <Input value={f.owner_name} onChange={(e) => setF({ ...f, owner_name: e.target.value })} required data-testid="client-owner-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Phone *</Label>
              <Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} required data-testid="client-phone" /></div>
            <div className="space-y-1.5"><Label>Email</Label>
              <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="client-email" /></div>
          </div>
          {isAdmin && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Brand *</Label>
                <Select value={f.brand} onValueChange={(v) => setF({ ...f, brand: v, owning_branch: "" })}>
                  <SelectTrigger data-testid="client-brand"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{Object.values(BRANDS).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1.5"><Label>Branch</Label>
                <Select value={f.owning_branch} onValueChange={(v) => setF({ ...f, owning_branch: v })} disabled={!f.brand}>
                  <SelectTrigger data-testid="client-branch"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{branchesFor(f.brand).map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>Address</Label>
            <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="client-address" /></div>

          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label>Pets</Label>
              <Button type="button" size="sm" variant="ghost" onClick={addPetRow} data-testid="client-add-pet"><Plus className="h-4 w-4 mr-1" />Add pet</Button>
            </div>
            {pets.length === 0 && <p className="text-xs text-muted-foreground">Optionally add the client's pets now — you can also add them later.</p>}
            <div className="space-y-3">
              {pets.map((pet, i) => (
                <div key={pet._k} className="rounded-lg border border-border/60 p-3 space-y-2" data-testid={`client-pet-row-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">Pet {i + 1}</span>
                    <button type="button" onClick={() => removePet(i)} className="text-xs text-destructive hover:underline" data-testid={`client-remove-pet-${i}`}>Remove</button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Pet name *" value={pet.pet_name} onChange={(e) => setPet(i, { pet_name: e.target.value })} data-testid={`client-pet-name-${i}`} />
                    <Input placeholder="Species" value={pet.species} onChange={(e) => setPet(i, { species: e.target.value })} />
                    <Input placeholder="Breed" value={pet.breed} onChange={(e) => setPet(i, { breed: e.target.value })} data-testid={`client-pet-breed-${i}`} />
                    <Input placeholder="Age (e.g. 2y)" value={pet.age} onChange={(e) => setPet(i, { age: e.target.value })} data-testid={`client-pet-age-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="save-client-btn" className="w-full">{busy ? "Saving…" : "Create Client"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Clients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sp, setSp] = useSearchParams();
  const needsSession = sp.get("needs_session") === "1";
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (query = "", s = sort) => {
    setLoading(true);
    try {
      const params = { sort: s };
      if (query) params.q = query;
      if (needsSession) params.needs_session = true;
      const { data } = await api.get("/clients", { params });
      setItems(data);
    } finally { setLoading(false); }
  }, [sort, needsSession]);

  useEffect(() => { load("", sort); }, [sort, load]);

  const onSearch = (e) => { e.preventDefault(); load(q, sort); };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Clients</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {needsSession ? "Showing clients whose next session isn't scheduled yet." : "Search by owner name, phone or email. Open a client to see all their pets and history."}
          </p>
          {needsSession && <button onClick={() => setSp({})} className="text-xs text-primary hover:underline mt-1" data-testid="clear-needs-session">Clear filter · show all clients</button>}
        </div>
        <NewClientDialog trigger={<Button data-testid="new-client-btn"><Plus className="h-4 w-4 mr-2" />New Client</Button>} onCreated={() => load(q, sort)} />
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        <form onSubmit={onSearch} className="flex gap-2 flex-1 min-w-[240px]">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="client-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients…" className="pl-9" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[190px]" data-testid="client-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Most recent</SelectItem>
            <SelectItem value="pets">Most pets</SelectItem>
            {isAdmin && <SelectItem value="aov">Highest AOV</SelectItem>}
            {isAdmin && <SelectItem value="spend">Highest spend</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      <Card className="p-0 border-border/60 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
          <div className="col-span-4">Owner</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Pets</div>
          {isAdmin ? <><div className="col-span-2 text-right">AOV</div><div className="col-span-2 text-right">Total spend</div></> : <div className="col-span-4 text-right">Brand</div>}
        </div>
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center text-muted-foreground"><Users className="h-10 w-10 mx-auto mb-3 opacity-40" />No clients found.</div>
        )}
        <div className="divide-y divide-border">
          {items.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`} data-testid={`client-row-${c.id}`}
                  className="grid grid-cols-12 px-5 py-4 items-center hover:bg-muted/40 transition-colors">
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate">{c.owner_name}</div>
                <div className="text-xs text-muted-foreground truncate">{c.email || brandName(c.brand)}{c.owning_branch ? ` · ${c.owning_branch}` : ""}</div>
              </div>
              <div className="col-span-2 text-sm tabular-nums">{c.phone}</div>
              <div className="col-span-2 text-sm flex items-center gap-1.5"><PawPrint className="h-3.5 w-3.5 text-primary" />{c.pets_count}</div>
              {isAdmin ? (
                <><div className="col-span-2 text-right text-sm font-medium">{money(c.aov)}</div>
                  <div className="col-span-2 text-right text-sm">{money(c.total_spend)}</div></>
              ) : (
                <div className="col-span-4 text-right text-sm text-muted-foreground">{brandName(c.brand)}</div>
              )}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
}
